<script>
import { get } from "lodash";
import { fandomBasicMixin } from "src/modules/fandom_mixins_module.js";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { HFarmCorporateNavbarMixin } from "../../modules/hfarm_corporate_mixins_module";
import MobileMenu from 'mobile-menu';
import NavbarAuxiliary from "navbar-auxiliary";
import SearchFullscreen from "search-fullscreen";

export default {
  mixins: [fandomBasicMixin, HFarmCorporateNavbarMixin],
  components: {
    MobileMenu,
    NavbarAuxiliary,
    SearchFullscreen
  },
  data() {
    return {
      fullscreenMenu: false,
      fullscreenSearch: false,
      fullscreenEntered: false,
      mobileMenu: null
    };
  },
  methods: {
    openFullScreenMenu() {
      this.fullscreenMenu = true;
      disableBodyScroll(this.$refs['mobile-menu']);
    },
    openFullScreenSearch() {
      this.fullscreenSearch = true;
    },
    closeFullscreen() {
      this.fullscreenMenu = false;
      this.fullscreenSearch = false;
      clearAllBodyScrollLocks();
      $(this.$el).off('keypress');
    },
    toggleFullScreen(menu) {
      if (this.fullscreenEntered && this.fullscreen && !this.fullscreenMenu) { // animation management
        this.fullscreenEntered = false;
      } else if (this.fullscreen) {
        Vue.set(this, 'mobileMenu', null);
        this.closeFullscreen();
      } else {
        if (!!menu) {
          Vue.set(this, 'mobileMenu', menu);
        }
        this.openFullScreenMenu();
      }
    },
    afterEnterFullscreen() {
      this.fullscreenEntered = true;
    },
    getContentPath(mainContentName, items) {
      const index = items.findIndex(item => get(item, ["url", "content"], false) === mainContentName);
      if (index != -1) {
        return ["items", index];
      } else {
        let result = null;
        items.forEach((item, idx) => {
          if (item.items && !result) {
            const path = this.getContentPath(mainContentName, item.items);
            if (path) {
              result = ["items", idx, ...path];
            }
          }
        });
        return result;
      }
    }
  },
  computed: { 
    fullscreen() {
      return this.fullscreenSearch || this.fullscreenMenu;
    },
    items() {
      return get(this.header, ["items"], []);
    },
    itemsRight() {
      return get(this.header, ["items_right"], []);
    },
    header() {
      return get(globalState, ["pageModel", "layout", "header"], {}); 
    },
    logoAlt() {
      return get(this.pageModel, ['seo', 'title'], '');
    },
    secondaryNavbar() {
      // example: ["items",2,"items",0,"items",3]
      let nav = this.getContentPath(this.mainContent.name, this.items);
      if (!!nav) {
        const navLevel = this.mainContent.navbar_level || this.header.navbar_max_level;
        if (!!navLevel && nav.length > ((parseInt(navLevel)-1)*2)) {
          nav = nav.slice(0, (parseInt(navLevel)-1)*2);
        }
        let result = get(this.header, nav, {});
        if (!result.items) {
          nav = nav.slice(0, nav.length - 4);
        }
        if (result.items && result.items.every(item => !item.items)) {
          nav = nav.slice(0, nav.length - 2);
        }
        return get(this.header, nav, {});
      }
      return false;
    }
  },
};
</script>

<template>
  <div class="sticky-top" id="main-navbar" :class="{'sticky-negative': secondaryNavbar}">
    <nav class="navbar navbar-expand-lg justify-content-between py-0 px-4 border-bottom" navbar-component>
      <a class="navbar-brand p-0 m-0" :href="applyContextToUrl('/')">
        <img :src="content.logo.url" class="navbar-logo" :alt="logoAlt"/>
      </a>
      <div class="collapse navbar-collapse flex-grow-0 h-100 mx-3" id="navbar" :class="{'faded': fullscreen}">
        <ul class="navbar-nav h-100 align-items-stretch">
          <li 
            class="nav-item px-3 d-flex align-items-center" 
            :class="{ 'active': isItemActive(item) }" 
            v-for="(item, index) in getItems(items)"
            :key="`item-${index}`" 
            :id="`item-${index}`"
          >
            <a
              :href="getItemHref(item)"
              :target="getTarget(item.url)"
              class="display-2 font-weight-extra-bold text-uppercase"
            >
              {{getItemTitle(item)}}
            </a>
            <div class="position-absolute submenu bg-gray-100" v-if="!!item.items">
              <div class="container">
                <div class="row justify-content-center w-100 py-5">
                  <div class="col" v-for="(section, sectionIndex) in getItems(item.items)" :key="`section-${sectionIndex}-${index}`">
                    <a
                      :href="getItemHref(section)"
                      :target="getTarget(section.url)"
                      class="text-uppercase mb-3 d-block"
                      :class="{'font-weight-extra-bold h5 mb-3': isTwoLevelsMenu(item)}"
                    >{{getItemTitle(section)}}</a>
                    <div
                      v-for="(subitem, subitemIndex) in getItems(section.items)"
                      :key="`subitem-${subitemIndex}-${sectionIndex}-${index}`"
                      class="mb-2"
                    >
                      <a :href="getItemHref(subitem)" :target="getTarget(subitem.url)">
                        {{getItemTitle(subitem)}}
                      </a>
                    </div>
                  </div>
                  <div class="col-12 d-flex justify-content-center pt-5 border-top mt-5" v-if="item.button">
                    <a :href="item.button.url" :target="getTarget(item.button)" class="btn btn-outline-light">
                      {{ft('globals.discover')}} {{item.button.title}}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div class="d-flex align-items-center h-100">
        <i id="search-icon" class="far fa-search cursor-pointer mr-3" :class="{'faded': fullscreen}" @click="openFullScreenSearch()"></i>
        <div class="collapse navbar-collapse flex-grow-0 h-100" id="navbar-right" :class="{'faded': fullscreen}">
          <ul class="navbar-nav h-100 align-items-stretch">
            <li 
            class="nav-item mr-3 d-flex align-items-center"
            :class="{ 'active': isItemActive(item) }"
            v-for="(item, index) in getItems(itemsRight)"
            :key="`item-right-${index}`"
            :id="`item-right-${index}`"
            >
              <a
                :href="getItemHref(item)"
                :target="getTarget(item.url)"
                class="font-weight-bold text-uppercase"
              >
                {{getItemTitle(item)}}
              </a>
            </li>
          </ul>
        </div>
        <a v-if="content.launch_link" :href="content.launch_link.url" :target="getTarget(content.launch_link)" class="launch-link d-flex align-items-center" :class="{'faded': fullscreen}">
          <img v-if="content.launch_logo" :src="content.launch_logo.url" class="navbar-logo" :alt="logoAlt"/>
        </a>
        <div
          id="hamburger-menu-icon"
          class="ml-4 d-lg-none"
          :class="[{'open': fullscreen}, (!!mobileMenu ? mobileMenu.theme : 'light')]"
          @click="toggleFullScreen({items: items, itemsRight: itemsRight, theme: 'light'})"
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <transition enter-active-class="animated slideInLeft faster" leave-active-class="animated slideOutLeft faster">
        <mobile-menu
          :depth="1"
          :max-depth="3"
          ref="mobile-menu"
          :items="mobileMenu.items"
          :itemsRight="mobileMenu.itemsRight"
          :theme="mobileMenu.theme"
          v-if="fullscreenMenu && !!mobileMenu"
        ></mobile-menu>
      </transition>
      <transition enter-active-class="animated slideInDown faster" leave-active-class="animated slideOutUp faster" v-on:after-enter="afterEnterFullscreen">
        <search-fullscreen
          v-if="fullscreenSearch"
          :content="content"
          :entered="fullscreenEntered"
          @toggle="toggleFullScreen"
        ></search-fullscreen>
      </transition>
    </nav>
    <navbar-auxiliary
      v-if="secondaryNavbar"
      :content="secondaryNavbar"
      :opt-container-content="header"
      @mobile-menu="toggleFullScreen"
    ></navbar-auxiliary>
  </div>
</template>

<style lang="scss">

.sticky-negative {
  top: -$header-height;
}

@include media-breakpoint-down(md) {
  .sticky-negative {
    top: -$header-height-sm;
  }
}

[navbar-component] {

  & {
    background-color: white;
    height: $header-height;
    z-index: $zindex-sticky + 1;
  }

  #navbar {
    opacity: 1;
    transition: all .5s;
  }

  #navbar.faded {
    opacity: 0;
  }

  .nav-item {
    letter-spacing: 1px;
    font-size: .75rem;

    a {
      color: inherit;
      text-decoration: none;
      transition: color .25s;
      font-weight: 400;
    }

    & > .submenu {
      left: 0;
      right: 0;
      top: 100%;
      height: 0;
      overflow: hidden;
    }

    &:hover > .submenu {
      transition: height .25s;
      height: auto;
    }

    &:hover > a,
    &:active > a,
    &:focus > a,
    &.active > a 
    & > .submenu a:hover {
      color: $primary;
    }
  }

  #search-icon {
    color: inherit;
    transition: color .25s;

    &:hover {
      color: $primary;
    }
  }

  .launch-link {
    transition: all .25s;

    &.faded {
      opacity: 0;
    }
  }

  .navbar-logo {
    height: 2.25rem;
    width: auto;
  }

  @include media-breakpoint-down(lg) {
    .display-2 {
      font-size: 2rem;
    }
  }

  @include media-breakpoint-down(md) {
    & {
      height: $header-height-sm;
    }
  }

  /* Haburger css */

  #hamburger-menu-icon {
    width: 1rem;
    height: .75rem;
    cursor: pointer;
    position: relative;
    z-index: $zindex-sticky + 100;
    transform: rotate(0deg) .5s ease-in-out;
  }

  #hamburger-menu-icon span {
    left: 0;
    opacity: 1;
    height: 2px;
    width: 100%;
    display: block;
    position: absolute;
    transition: all .25s ease-in-out;
    transform: rotate(0deg) .25s ease-in-out;
  }

  #hamburger-menu-icon.dark span {
    background: $white;
  }

  #hamburger-menu-icon.dark:hover span {
    background-color: $gray-300;
  }

  #hamburger-menu-icon.light span {
    background: $gray-900;
  }

  #hamburger-menu-icon.light:hover span {
    background-color: $primary;
  }

  #hamburger-menu-icon span:nth-child(1) {
    top: 0px;
  }

  #hamburger-menu-icon span:nth-child(2) {
    top: 5px;
  }

  #hamburger-menu-icon span:nth-child(3) {
    top: 10px;
  }

  #hamburger-menu-icon.open {
    display: block !important;
  }

  #hamburger-menu-icon.open span:nth-child(1) {
    top: 5px;
    transform: rotate(135deg);
  }

  #hamburger-menu-icon.open span:nth-child(2) {
    opacity: 0;
    left: -1rem;
  }

  #hamburger-menu-icon.open span:nth-child(3) {
    top: 5px;
    transform: rotate(-135deg);
  }
  
}
</style>

